import { Avatar, type AvatarProps, Box } from "@mui/material";

interface AssistantAvatarProps extends AvatarProps {
  size?: number;
}

/**
 * Circuit Forge assistant avatar — the brand "Pulse" mark on a transparent
 * disc. Mark geometry is kept in sync with `components/Logo/index.tsx`
 * (and the brand-handoff SVG assets) so the mark next to each chat
 * response matches the one in the sidebar verbatim.
 *
 * The graticule is dropped here even though the avatar is large enough
 * to show it — at the avatar's typical render size (32 px) the graticule
 * lines crowd the disc edge and read as noise. Reverting to "pulse only"
 * matches the favicon treatment and keeps the avatar legible against
 * the busy chat-row background.
 */
export function AssistantAvatar({
  size = 32,
  sx,
  ...rest
}: AssistantAvatarProps) {
  // Mark covers ~62% of the avatar diameter — same visual weight the
  // previous chip mark had, so existing layout spacing still reads
  // correctly next to the "Circuit Forge" overline label.
  const markSize = Math.round(size * 0.62);
  return (
    <Avatar
      alt="Circuit Forge assistant"
      sx={[
        {
          width: size,
          height: size,
          // Transparent ground + 1px ring; the mark is rendered in
          // primary.main so it picks up the theme's editorial amber
          // without the AssistantAvatar needing to know the brand hex.
          bgcolor: "transparent",
          border: "1px solid",
          borderColor: "border.default",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      <Box
        component="svg"
        viewBox="0 0 1024 1024"
        sx={{
          width: markSize,
          height: markSize,
          color: "editorial.main",
        }}
        aria-hidden="true"
      >
        {/* Pulse path — verbatim from the brand handoff. Stroke width
            tuned for the avatar size: thicker than the full mark (40)
            because the avatar typically renders at 32–48 px. */}
        <path
          d="M 200 640 L 380 640 L 380 360 L 644 360 L 644 640 L 824 640"
          fill="none"
          stroke="currentColor"
          strokeWidth="80"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    </Avatar>
  );
}
